import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Draggable from 'react-draggable';
import { GlobalContext } from '../../Context/GlobalContext';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { AxiosService } from '../../Services/AxiosService';
import { STATUS_RESOLVED } from '../../Constants/Global';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * A reusable Paper component for the draggable dialog. This component is used to wrap the dialog content
 * and enable dragging functionality.
 * 
 * @component
 * @param {Object} props - The component props.
 * @param {React.RefObject} props.nodeRef - A ref to the Paper component.
 * @returns {JSX.Element} A draggable Paper component.
 */
function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper 
        {...props} 
        ref={nodeRef} 
        className="formPaperAlert" 
        sx={{
          overflow: "auto",
          maxWidth: "420px",
          borderRadius: "30px"
        }}/>
    </Draggable>
  );
}

/**
 * A reusable alert dialog component that displays a draggable dialog with a title, message, and action buttons.
 * The dialog supports different types of actions (e.g., resolving tasks, removing tasks) and displays
 * success or error messages based on the API response.
 * 
 * @component
 * @returns {JSX.Element} The rendered alert dialog component.
 * @example
 * <AlertDialog />
 */
function AlertDialog() {
  const {
    alertOpen, 
    setAlertOpen,
    alertType,
    alertTitle,
    alertMessage,
    setModalOpen,
    setModalAltaCentroOpen,
    setProcessModalOpen, 
    setAlertBarSeverity,
    setAlertBarMessage,
    setAlertBarOpen,
    modalData,
    setRefreshTable
  } = React.useContext(GlobalContext);
  const [loadingRequest, setLoadingRequest] = React.useState(false);
  const navigate = useNavigate();
  const auth = useAuth();
  const { pathname } = useLocation();

  /**
   * Handles the "Accept" button click. Closes the dialog and performs an action based on the `alertType`.
   */
  const handleAccept = () => {   
    setAlertOpen(false);
    switch (alertType) {
      case "resolve_task":
        resolveTask();
        break;

      case "resolve_subtask":
        resolveSubtask();
        break;

      case "remove_task":
        removeTask();
        break;

      default:
        break;
    }
  };

  /**
   * Handles closing the dialog when the user clicks outside, presses the Escape key,
   * or interacts with specific elements.
   * 
   * @param {Event} event - The event triggering the close action.
   */
  const handleClose = (event) => {     
    if (event.nativeEvent.code === "Escape"
      || (event.target.tagName !== "H2"
        && event.target.tagName !== "LI"
        && event.target.tagName !== "DIV")){
      setAlertOpen(false);
    }
  };

  /**
   * Refreshes the current view based on the application's pathname.
   * - For "/calendar" route: Forces a navigation refresh by temporarily redirecting to "/#"
   * - For "/processes" route: Increments the refresh counter to trigger a table re-render
   * - For all other routes: No action is taken
   *
   * @function refreshView
   * @returns {void}
   *
   * @example
   * // When on calendar view:
   * refreshView(); // Will force refresh the calendar
   *
   * @example
   * // When on processes view:
   * refreshView(); // Will increment refresh counter causing table to reload
   */
  const refreshView = () => {
    switch (pathname) {
      case "/calendar":
        navigate("/#");
        break;

      case "/processes":
        setRefreshTable(prev => prev + 1);
        break;

      default:
        break;
    }
  };

  /**
   * Resolves a task by updating its status to "Resolved" via an API call.
   * Displays a success or error message based on the API response.
   */
  const resolveTask = () => {
    setLoadingRequest(true);
    const requestBody =  {
      statusId: STATUS_RESOLVED
    };

    AxiosService.update_task(auth.user?.access_token, modalData.type === "process"? modalData.id : modalData.label, requestBody)
    .then((response) => {
      setLoadingRequest(false);
      setAlertBarSeverity("success");
      setAlertBarMessage(response.data.message);
      setAlertBarOpen(true);
      setProcessModalOpen(false);
      refreshView();
    })
    .catch((err) => {
      setAlertBarSeverity("error");
      setAlertBarMessage(`${err.message} (${err.response.statusText}): ${err.response.data.error}`);
      setAlertBarOpen(true);
    })
    .finally(() => {
      setLoadingRequest(false);
    });
  };

  /**
   * Resolves a subtask by updating its status to "Resolved" via an API call.
   * Displays a success or error message based on the API response.
   */
  const resolveSubtask = () => {
    setLoadingRequest(true);
    const requestBody =  {
      statusId: STATUS_RESOLVED
    };

    AxiosService.update_subtask(auth.user?.access_token, modalData.id, requestBody)
    .then((response) => {
      setAlertBarSeverity("success");
      setAlertBarMessage(response.data.message);
      setAlertBarOpen(true);
      setModalOpen(false);
      setModalAltaCentroOpen(false);
      refreshView();
    })
    .catch((err) => {
      setAlertBarSeverity("error");
      setAlertBarMessage(`${err.message} (${err.response.statusText}): ${err.response.data.error}`);
      setAlertBarOpen(true);
    })
    .finally(() => {
      setLoadingRequest(false);
    });
  };

  /**
   * Removes a task by deleting it via an API call.
   * Displays a success or error message based on the API response.
   */
  const removeTask = () => {
    setLoadingRequest(true);
    AxiosService.delete_task(auth.user?.access_token, modalData.type === "process"? modalData.id : modalData.label)
    .then((response) => {
      setAlertBarSeverity("success");
      setAlertBarMessage(response.data.message);
      setAlertBarOpen(true);
      setProcessModalOpen(false);
      refreshView();
    })
    .catch((err) => {
      setAlertBarSeverity("error");
      setAlertBarMessage(`${err.message} (${err.response.statusText}): ${err.response.data.error}`);
      setAlertBarOpen(true);
    })
    .finally(() => {
      setLoadingRequest(false);
    });
  }
  
  let popupTitle = alertTitle? alertTitle : "Alert";

  return (
    <> 
    <Dialog
      open={alertOpen}
      onClose={handleClose}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle style={{ cursor: 'unset' }} id="draggable-dialog-title">
        { popupTitle }
      </DialogTitle>
    <DialogContent sx={{ maxHeight: "62vh" }}>
      { alertMessage }
      </DialogContent>
      <DialogActions>
        {alertType !== "permission_denied" &&
        <Button color="error" sx={{ marginRight: "auto", fontWeight:'fontWeightBold' }} onClick={handleClose}>
          <CloseIcon/> Cancel
        </Button>
        }
        <Button color="success" loading={loadingRequest} sx={{ fontWeight:'fontWeightBold' }} onClick={handleAccept}>
          <DoneIcon /> Accept
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
}

export default AlertDialog;
